import { Button, DatePicker, Input, Popover, Select } from "antd";
import { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import moment from "moment";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import InfoIcon from "../../../assets/icons/InfoIcon";
import IoCloseCircleOutline from "../../../assets/icons/IoCloseCircleOutline";
import IoMdCheckmarkCircle from "../../../assets/icons/IoMdCheckmarkCircle";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import AddLeaveRequestModal from "../../../components/Leave/LeaveRequest/AddLeaveRequestModal";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import CustomTable from "../../../components/ui/CustomTable";
import colors from "../../../constants/colors";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetLeaveApplicationListQuery } from "../../../features/leaveApplication/leaveApplicationApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import { useLeaveApplicationReportMutation } from "../../../features/report/reportApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  GetLeaveApplicationParams,
  GetLeaveApplicationResponseType,
} from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../utils/utils";

const { RangePicker } = DatePicker;

enum Status {
  accepted = "status-success",
  pending = "status-warning",
  rejected = "status-danger",
  canceled = "status-danger",
}

export default function LeaveRequests() {
  const location = useLocation();
  const [isLineManager, setIsLineManager] = useState(true);
  const { authInfo } = useAppSelector((state) => state?.auth);
  const { query } = useQueryParams();
  const { open, handleClose, handleOpen } = useModal<
    undefined,
    "REPORT_REQUEST" | "ADD_REQUEST"
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<GetLeaveApplicationParams>({
    defaultFilter: {
      status:
        (query.status as GetLeaveApplicationParams["status"]) || undefined,
      from_date:
        (query.from_date as GetLeaveApplicationParams["from_date"]) ||
        undefined,
      to_date:
        (query.to_date as GetLeaveApplicationParams["to_date"]) || undefined,
      approver_status:
        (query.approver_status as GetLeaveApplicationParams["approver_status"]) ||
        undefined,
      approver_id:
        (query.approver_id as GetLeaveApplicationParams["approver_id"]) ||
        undefined,
      platform:
        (query.platform as GetLeaveApplicationParams["platform"]) || undefined,
    },
  });

  const {
    isFetching,
    isLoading: isLeaveApplicationDataLoading,
    data: leaveApplicationListData,
    isError: isLeaveApplicationListError,
    error: leaveApplicationError,
    // refetch,
  } = useGetLeaveApplicationListQuery({
    params: {
      ...filter,
      approver_id: isLineManager ? String(authInfo?.id) : undefined,
      search_keyword: keyword,
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
    },
  });

  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: leaveTypeListData,
    isError: isLeaveTypeError,
    error: leaveTypeError,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
      skip: 0,
      limit: 0,
    },
  });
  const [
    leaveApplicationReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useLeaveApplicationReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(
    isLeaveApplicationListError,
    leaveApplicationError,
    "Failed fetching leave application list"
  );
  useError(
    isDepartmentError,
    departmentError,
    "Failed fetching department list"
  );
  useError(isLeaveTypeError, leaveTypeError, "Get leave type data fail");

  const columns: TableProps<
    GetLeaveApplicationResponseType["data"]["leave_applications"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: () => <span className="text-nowrap mr-2">Applied Date</span>,
      dataIndex: "created_at",
      width: 130,
      render: (val) => (
        <div>
          {/* <div>{formatUTCDate(val)}</div> */}
          <div>{moment.utc(val).local().format("DD MMM, YYYY")}</div>
          <div>{moment.utc(val).local().format("hh:mm a")}</div>
          {/* <div>{UTCTimeFormat(val)}</div> */}
        </div>
      ),
    },
    {
      title: () => <span className="text-nowrap mr-2">Employee ID</span>,
      dataIndex: "business_member",
      render: (val) => val.employee_id || "n/a",
      width: 120,
    },
    {
      title: <span className="text-nowrap mr-2">Employee Name</span>,
      dataIndex: "business_member",
      sorter: (a, b) =>
        a?.business_member?.name?.localeCompare(b?.business_member?.name),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.name,
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "business_member",
      render: (val) => val?.profile?.email || "n/a",
    },
    {
      title: <span className="text-nowrap mr-2">Department</span>,
      dataIndex: "business_member",
      render: (val) => val.role.department.name,
    },
    {
      title: <span className="text-nowrap mr-2">Leave Type</span>,
      dataIndex: "leave_type",
      render: (val) => val.title,
    },
    {
      title: <span className="text-nowrap mr-2">Leave Days</span>,
      dataIndex: "total_days",
      // render: (val) => calculateDateDifference(val.start_date, val.end_date),
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status, record) => {
        // const approverList = record?.approvers?.filter(
        //   (item) => item?.status !== "pending"
        // );
        const content = (
          <ul className="flex flex-col gap-2 text-white">
            {record?.approvers?.map((approver) => (
              <li className="flex items-center gap-1" key={approver?.id}>
                {" "}
                <span className="w-4">
                  {approver?.status === "accepted" ? (
                    <IoMdCheckmarkCircle
                      style={{ color: colors?.success?.color }}
                      size={16}
                    />
                  ) : approver?.status === "rejected" ||
                    approver?.status === "cancelled" ? (
                    <IoCloseCircleOutline
                      style={{ color: colors?.danger?.color }}
                      size={16}
                    />
                  ) : null}
                </span>
                {approver?.approver?.name}
              </li>
            ))}
          </ul>
        );

        return (
          <div className="flex items-center gap-1">
            <span className={Status[val]}>
              {capitalize(val === "accepted" ? "approved" : val)}
            </span>
            {(val === "canceled" || val === "rejected") &&
            record?.approvers?.length ? (
              <Popover
                content={content}
                trigger="hover"
                overlayClassName="dark-popup-tooltip"
              >
                <Button
                  className="action-button border-0"
                  size="small"
                  icon={<InfoIcon />}
                />
              </Popover>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Action",
      render: (val) => (
        <>
          <Link to={`${location.pathname}/view/${val.id}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </>
      ),
    },
  ];

  return (
    <SidebarLayout title="Leave Request">
      <div className="py-2 px-3 my-4 flex justify-between items-center text-[#084298] bg-[#CFE2FF] flex-wrap rounded-md">
        <span>
          {isLineManager
            ? `You are viewing this list as an approver. To monitor company wide all leave requests, change into ${
                authInfo?.role === "Manager" ? "line manager" : "super admin"
              } mode.`
            : `You are viewing this list as a ${
                authInfo?.role === "Manager" ? "line manager" : "super admin"
              }. To monitor company wide all leave requests, change into approver mode.`}
        </span>
        <Button
          className="flex gap-2 items-center !border-[#4A72FF] !text-[#4A72FF]"
          onClick={() => {
            setIsLineManager(!isLineManager);
          }}
        >
          <ViewIcon />
          {isLineManager
            ? authInfo?.role === "Manager"
              ? "View as Line Manager"
              : "View as Super Admin"
            : "View as Approver"}
        </Button>
      </div>
      <CustomTable
        loading={isLeaveApplicationDataLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={leaveApplicationListData?.data.total_leave_applications}
        dataSource={addKeyInArray(
          leaveApplicationListData?.data.leave_applications
        )}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search by name, id, email"
              style={{ width: 220 }}
              onChange={handleSearch}
            />
            <RangePicker
              format={dateFormat}
              style={{ width: 260 }}
              suffixIcon={<DateIcon />}
              value={[
                filter?.applied_from_date
                  ? dayjs(filter?.applied_from_date)
                  : null,
                filter?.applied_to_date ? dayjs(filter?.applied_to_date) : null,
              ]}
              onChange={(e) => {
                handleFilter("applied_from_date", e?.[0]?.format(dbDateFormat));
                handleFilter("applied_to_date", e?.[1]?.format(dbDateFormat));
              }}
            />
            <Select
              allowClear
              showSearch
              placeholder="Leave Type"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                leaveTypeListData?.data?.leave_types || [],
                "title",
                "id"
              )}
              value={filter?.leave_type_id}
              onChange={(e) => handleFilter("leave_type_id", e)}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Department"
              filterOption={selectSearchOption}
              value={filter?.department_id}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              onChange={(e) => handleFilter("department_id", e)}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={leaveStatusList}
              value={filter?.status}
              onChange={(e) => handleFilter("status", e)}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />

            {/* <Button
              className="download-button"
              icon={<DownloadIcon />}
              loading={excelLoading}
              onClick={handleDownload}
            >
              Export
            </Button> */}
            <Button
              onClick={() => handleOpen({ type: "ADD_REQUEST" })}
              type="primary"
              icon={<AddIcon />}
            >
              Add Request
            </Button>
            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
          </>
        }
      />

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            leaveApplicationReport({
              body: {
                name: "Leave request report",
              },
              params: {
                ...filter,
                search_keyword: keyword,
                skip: 0,
                limit: 0,
                approver_id: isLineManager ? authInfo?.id : undefined,
                applied_from_date: params?.start_date,
                applied_to_date: params?.end_date,
                manager_id:
                  authInfo?.role === "Manager" && !isLineManager
                    ? authInfo?.id
                    : undefined,
              },
            });
          }}
        />
      )}
      {open.type === "ADD_REQUEST" && (
        <AddLeaveRequestModal open={open.open} handleClose={handleClose} />
      )}
    </SidebarLayout>
  );
}

export const leaveStatusList = [
  {
    label: "Approved",
    value: "accepted",
  },
  {
    label: "Pending",
    value: "pending",
  },
  {
    label: "Rejected",
    value: "rejected",
  },
  {
    label: "Canceled",
    value: "canceled",
  },
];
